import { useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Switch } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  clearPlayerCard,
  getPlayerCard,
  savePlayerCard,
} from "../../../services/PlayerCardService";
import { captureComparisonImage } from "../../../utils/captureComparisonImage";
import LoadingSpinner from "../../LoadingSpinner";
import PlayerCard from "./player-card";
import ProfileCardReadyStep from "./profile-card-ready-step";
import {
  OPTION_GROUPS,
  buildPreviewCard,
  selectionsFromSaved,
  selectionsToOptions,
} from "./player-card-options";

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

function RoundCheckbox({ checked, disabled, onChange, label }) {
  return (
    <label
      className={`flex min-w-0 items-center gap-2 ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden="true"
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${checked ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"}`}
      >
        {checked && (
          <FontAwesomeIcon icon={faCheck} className="h-2 w-2 text-white" />
        )}
      </span>
      <span className="truncate text-xs text-gray-800">{label}</span>
    </label>
  );
}

function ShowScoreToggle({ checked, disabled, onChange, label }) {
  return (
    <div className={`flex items-center gap-1 ${disabled ? "opacity-40" : ""}`}>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={`Show ${label} score`}
        className="group relative flex h-4 w-7 shrink-0 cursor-pointer rounded-full bg-gray-300 p-0.5 transition-colors data-[checked]:bg-blue-600 data-[disabled]:cursor-not-allowed"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow transition group-data-[checked]:translate-x-3"
        />
      </Switch>
      <span className="text-[9px] text-gray-500">Show Score</span>
    </div>
  );
}

function OptionGroup({ group, available, selections, onToggle, onShowScore }) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-medium text-gray-900">{group.title}</h3>
      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
        {group.options.map((option) => {
          const disabled = !available.has(option.key);
          const selection = selections[option.key];
          return (
            <li
              key={option.key}
              className="flex items-center justify-between gap-2 px-3 py-1.5"
            >
              <RoundCheckbox
                label={option.label}
                checked={!!selection}
                disabled={disabled}
                onChange={(checked) => onToggle(option.key, checked)}
              />
              {group.hasScore && (
                <ShowScoreToggle
                  label={option.label}
                  checked={!!selection?.showScore}
                  disabled={!selection}
                  onChange={(showScore) => onShowScore(option.key, showScore)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ClearCardConfirm({ open, clearing, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel} className="relative z-[60]">
      <div className="fixed inset-0 bg-gray-800/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-96 rounded-md bg-white p-6 shadow-lg">
          <DialogTitle className="mb-3 text-lg font-semibold text-gray-900">
            Are you sure?
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Your profile card will be removed. Anyone who opens a card link
            you've shared will see that you no longer have a card.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={clearing}
              className="rounded border px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={clearing}
              className="rounded bg-red-600 px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              {clearing ? "Clearing..." : "Clear Card"}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

/**
 * "Create Your Profile Card" modal. Step 1 lets the athlete choose what goes on
 * their card and saves it; step 2 (ProfileCardReadyStep) downloads or shares it.
 */
export default function ProfileCardModal({ open, onClose }) {
  const apiInstance = useAxiosInstance();
  const cardRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [playerCard, setPlayerCard] = useState(null);
  const [selections, setSelections] = useState({});
  const [includeQrCode, setIncludeQrCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    setStep(1);
    setSavedCard(null);
    setLoadError(null);
    setLoading(true);

    getPlayerCard(apiInstance)
      .then((data) => {
        if (cancelled) return;
        setPlayerCard(data);
        setSelections(
          selectionsFromSaved(data?.options, data?.availableOptions),
        );
        setIncludeQrCode(!!data?.includeQrCode);
      })
      .catch((error) => {
        if (cancelled) return;
        setPlayerCard(null);
        setLoadError(
          errorMessage(
            error,
            "We couldn't load your profile card. Please try again.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // apiInstance is recreated on every render; the card only reloads on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const available = new Set(playerCard?.availableOptions ?? []);
  const hasSelection = Object.keys(selections).length > 0;
  const canGenerate = (hasSelection || includeQrCode) && !saving;
  const previewCard = buildPreviewCard(
    playerCard?.cardData,
    selections,
    includeQrCode,
  );

  const handleToggle = (key, checked) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (checked) next[key] = { showScore: true };
      else delete next[key];
      return next;
    });
  };

  const handleShowScore = (key, showScore) => {
    setSelections((prev) =>
      prev[key] ? { ...prev, [key]: { showScore } } : prev,
    );
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setSaving(true);
    try {
      const imageData = await captureComparisonImage(cardRef);
      const saved = await savePlayerCard(apiInstance, {
        options: selectionsToOptions(selections),
        includeQrCode,
        imageData,
      });
      setSavedCard({ token: saved?.token, imageUrl: saved?.imageUrl });
      setStep(2);
    } catch (error) {
      toast.error(
        errorMessage(
          error,
          "We couldn't generate your card. Please try again.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearPlayerCard(apiInstance);
      setConfirmClear(false);
      onClose();
      toast.success("Your profile card was cleared.");
    } catch (error) {
      toast.error(
        errorMessage(error, "We couldn't clear your card. Please try again."),
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-800/50" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-[960px] rounded-lg bg-white p-6 shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  {step === 1
                    ? "Create Your Profile Card"
                    : "Your Profile Card is Ready!"}
                </DialogTitle>
                <p className="mt-1 text-sm text-gray-600">
                  {step === 1
                    ? "Choose the information you'd like to include in your card."
                    : "Download or share it directly to social media."}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-700">Step: {step} of 2</span>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="text-gray-600 hover:text-black"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </button>
              </div>
            </div>

            {step === 2 ? (
              <ProfileCardReadyStep
                token={savedCard?.token}
                imageUrl={savedCard?.imageUrl}
                firstName={playerCard?.cardData?.firstName}
                lastName={playerCard?.cardData?.lastName}
                onClose={onClose}
              />
            ) : loading ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : loadError ? (
              <div className="mt-6">
                <p className="text-sm text-red-600">{loadError}</p>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-5 flex flex-col gap-5 lg:flex-row">
                  <div className="flex w-full shrink-0 flex-col gap-3 lg:w-56">
                    {OPTION_GROUPS.map((group) => (
                      <OptionGroup
                        key={group.title}
                        group={group}
                        available={available}
                        selections={selections}
                        onToggle={handleToggle}
                        onShowScore={handleShowScore}
                      />
                    ))}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="overflow-x-auto">
                      <PlayerCard
                        ref={cardRef}
                        card={previewCard}
                        empty={!hasSelection && !includeQrCode}
                      />
                    </div>
                    <label className="mt-4 flex cursor-pointer items-start gap-2">
                      <input
                        type="checkbox"
                        checked={includeQrCode}
                        onChange={(e) => setIncludeQrCode(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      />
                      <span>
                        <span className="block text-sm text-gray-900">
                          Include QR Code
                        </span>
                        <span className="block text-[10px] text-gray-500">
                          Links to your public profile
                        </span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3">
                  <div>
                    {playerCard?.hasCard && (
                      <button
                        type="button"
                        onClick={() => setConfirmClear(true)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear Card
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded border px-4 py-2 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={!canGenerate}
                      className="rounded bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-red-200"
                    >
                      {saving ? "Generating..." : "Generate Card"}
                    </button>
                  </div>
                </div>
              </>
            )}

            <ClearCardConfirm
              open={confirmClear}
              clearing={clearing}
              onCancel={() => setConfirmClear(false)}
              onConfirm={handleClear}
            />
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
